/**
 * Feedback Service - Interface with backend for AI evaluation results
 */
import { api } from "../lib/api";

export interface DimensionScore {
  dimension_id: number;
  dimension_label: string;
  dimension_color: string;
  ai_score?: number; // 1-3 scale
  ai_reasoning?: string;
  ai_strengths?: string[];
  ai_improvements?: string[];
  ai_examples?: string;
  ai_processing_status: string;
  personal_score: number;
}

export interface InstructorSuggestion {
  suggestion_text: string;
  updated_at: string;
}

export interface ComparisonAnalysis {
  previous_submission_id: string;
  current_submission_id: string;
  overall_improvement: "improved" | "regressed" | "unchanged";
  previous_avg_score: number;
  current_avg_score: number;
  score_delta: number;
  dimension_comparisons: Array<{
    dimension_id: number;
    dimension_name: string;
    previous_score: number;
    current_score: number;
    score_change: number;
    improvement_summary: string;
  }>;
  summary: string;
  key_improvements: string[];
  key_regressions: string[];
}

export interface Submission {
  id: string;
  project_id: string;
  project_title: string;
  name: string;
  iteration_number: number;
  total_submissions: number;
  previous_submission_id?: string | null;
  comparison_analysis?: ComparisonAnalysis | null;
  submitted_at: string;
  change_notes?: string | null;
  ai_processing_status: string;
  ai_processing_error?: string;
  ai_overall_summary?: string;
  ai_overall_strengths?: string[];
  ai_priority_improvements?: string[];
  ai_estimated_level?: string;
  essay_text?: string;
  file_urls?: string[];
  file_names?: string[];
  instructor_suggestion?: InstructorSuggestion | null;
}

export interface FeedbackResponse {
  submission: Submission;
  dimensions: DimensionScore[];
}

export type SubmissionChatRole = "user" | "assistant";

export interface SubmissionChatSession {
  id: string;
  submission_id: string;
  user_id: string;
  scope: "user" | "group";
  scope_key: string;
  prompt_template_key: string;
  prompt_template_version: number;
  status: "active" | "cleared";
  active_generation_id: string | null;
  generation_started_at: string | null;
  generation_started_by: string | null;
  cleared_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmissionChatMessage {
  id: string;
  session_id: string;
  role: SubmissionChatRole;
  content: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface SubmissionChatResponse {
  session: SubmissionChatSession;
  messages: SubmissionChatMessage[];
}

export interface SendSubmissionChatMessageResponse
  extends SubmissionChatResponse {
  user_message: SubmissionChatMessage;
  assistant_message: SubmissionChatMessage;
}

export interface SubmissionChatStreamSessionEvent {
  type: "session";
  session: SubmissionChatSession;
  user_message: SubmissionChatMessage;
  generation_id: string;
}

export interface SubmissionChatStreamContentEvent {
  type: "content";
  session_id: string;
  generation_id: string;
  content: string;
}

export interface SubmissionChatStreamDoneEvent {
  type: "done";
  session: SubmissionChatSession;
  messages: SubmissionChatMessage[];
  assistant_message: SubmissionChatMessage;
  generation_id: string;
}

export interface SubmissionChatStreamErrorEvent {
  type: "error";
  message: string;
  session: SubmissionChatSession;
  messages: SubmissionChatMessage[];
  assistant_message: SubmissionChatMessage;
  generation_id: string;
}

export interface SubmissionChatActiveGeneration {
  generation_id: string;
  started_by_user_id: string | null;
  started_by_display_name: string | null;
  started_at: string;
  assistant_content: string;
}

export interface SubmissionChatSnapshotEvent {
  type: "snapshot";
  session: SubmissionChatSession;
  messages: SubmissionChatMessage[];
  active_generation: SubmissionChatActiveGeneration | null;
}

export interface SubmissionChatPingEvent {
  type: "ping";
  timestamp: string;
}

export type SubmissionChatStreamEvent =
  | SubmissionChatStreamSessionEvent
  | SubmissionChatStreamContentEvent
  | SubmissionChatStreamDoneEvent
  | SubmissionChatStreamErrorEvent
  | SubmissionChatSnapshotEvent
  | SubmissionChatPingEvent;

export interface SubmissionChatStreamCallbacks {
  onSnapshot?: (event: SubmissionChatSnapshotEvent) => void;
  onSession?: (event: SubmissionChatStreamSessionEvent) => void;
  onContent?: (event: SubmissionChatStreamContentEvent) => void;
  onDone?: (event: SubmissionChatStreamDoneEvent) => void;
  onError?: (event: SubmissionChatStreamErrorEvent) => void;
  onPing?: (event: SubmissionChatPingEvent) => void;
}

const isSubmissionChatMessage = (
  value: unknown,
): value is SubmissionChatMessage => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.session_id === "string" &&
    (candidate.role === "user" || candidate.role === "assistant") &&
    typeof candidate.content === "string" &&
    typeof candidate.created_at === "string"
  );
};

const isSubmissionChatSession = (
  value: unknown,
): value is SubmissionChatSession => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.submission_id === "string" &&
    typeof candidate.user_id === "string" &&
    (candidate.scope === "user" || candidate.scope === "group") &&
    typeof candidate.scope_key === "string" &&
    typeof candidate.prompt_template_key === "string" &&
    typeof candidate.prompt_template_version === "number" &&
    (candidate.status === "active" || candidate.status === "cleared") &&
    (candidate.active_generation_id === null ||
      typeof candidate.active_generation_id === "string") &&
    (candidate.generation_started_at === null ||
      typeof candidate.generation_started_at === "string") &&
    (candidate.generation_started_by === null ||
      typeof candidate.generation_started_by === "string") &&
    typeof candidate.created_at === "string" &&
    typeof candidate.updated_at === "string"
  );
};

const isSubmissionChatActiveGeneration = (
  value: unknown,
): value is SubmissionChatActiveGeneration => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.generation_id === "string" &&
    (candidate.started_by_user_id === null ||
      typeof candidate.started_by_user_id === "string") &&
    (candidate.started_by_display_name === null ||
      typeof candidate.started_by_display_name === "string") &&
    typeof candidate.started_at === "string" &&
    typeof candidate.assistant_content === "string"
  );
};

const isSubmissionChatStreamEvent = (
  value: unknown,
): value is SubmissionChatStreamEvent => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  if (candidate.type === "ping") {
    return typeof candidate.timestamp === "string";
  }

  if (candidate.type === "content") {
    return (
      typeof candidate.session_id === "string" &&
      typeof candidate.generation_id === "string" &&
      typeof candidate.content === "string"
    );
  }

  if (candidate.type === "snapshot") {
    return (
      isSubmissionChatSession(candidate.session) &&
      Array.isArray(candidate.messages) &&
      candidate.messages.every(isSubmissionChatMessage) &&
      (candidate.active_generation === null ||
        isSubmissionChatActiveGeneration(candidate.active_generation))
    );
  }

  if (candidate.type === "session") {
    return (
      isSubmissionChatSession(candidate.session) &&
      isSubmissionChatMessage(candidate.user_message) &&
      typeof candidate.generation_id === "string"
    );
  }

  if (candidate.type === "done") {
    return (
      isSubmissionChatSession(candidate.session) &&
      Array.isArray(candidate.messages) &&
      candidate.messages.every(isSubmissionChatMessage) &&
      isSubmissionChatMessage(candidate.assistant_message) &&
      typeof candidate.generation_id === "string"
    );
  }

  if (candidate.type === "error") {
    return (
      typeof candidate.message === "string" &&
      isSubmissionChatSession(candidate.session) &&
      Array.isArray(candidate.messages) &&
      candidate.messages.every(isSubmissionChatMessage) &&
      isSubmissionChatMessage(candidate.assistant_message) &&
      typeof candidate.generation_id === "string"
    );
  }

  return false;
};

const parseSubmissionChatStreamEvent = (
  line: string,
): SubmissionChatStreamEvent | null => {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }

  const parsed: unknown = JSON.parse(trimmed);
  if (!isSubmissionChatStreamEvent(parsed)) {
    throw new Error("Chat stream returned an invalid event");
  }

  return parsed;
};

const dispatchSubmissionChatStreamEvent = (
  event: SubmissionChatStreamEvent,
  callbacks: SubmissionChatStreamCallbacks,
): void => {
  if (event.type === "snapshot") {
    callbacks.onSnapshot?.(event);
    return;
  }

  if (event.type === "ping") {
    callbacks.onPing?.(event);
    return;
  }

  if (event.type === "session") {
    callbacks.onSession?.(event);
    return;
  }

  if (event.type === "content") {
    callbacks.onContent?.(event);
    return;
  }

  if (event.type === "done") {
    callbacks.onDone?.(event);
    return;
  }

  callbacks.onError?.(event);
};

export const feedbackService = {
  /**
   * Get AI feedback for a submission from backend
   * @param submissionId - UUID of the project submission
   */
  async getFeedback(submissionId: string): Promise<FeedbackResponse> {
    try {
      // api.get already unwraps data.data, so we get FeedbackResponse directly
      const data = await api.get<FeedbackResponse>(
        `/projects/submissions/${submissionId}/feedback`
      );
      return data;
    } catch (error) {
      console.error("Error fetching feedback:", error);
      throw error;
    }
  },

  /**
   * Poll for feedback with exponential backoff
   * @param submissionId - UUID of the project submission
   * @param onUpdate - Callback for status updates
   * @param maxAttempts - Maximum polling attempts (default: 30)
   */
  async pollForFeedback(
    submissionId: string,
    onUpdate: (status: string, data?: FeedbackResponse) => void,
    maxAttempts = 30
  ): Promise<FeedbackResponse | null> {
    let attempts = 0;
    const baseDelay = 2000; // Start with 2 seconds

    while (attempts < maxAttempts) {
      try {
        const data = await this.getFeedback(submissionId);

        onUpdate(data.submission.ai_processing_status, data);

        // If completed or failed, return result
        if (
          data.submission.ai_processing_status === "completed" ||
          data.submission.ai_processing_status === "failed"
        ) {
          return data;
        }

        // Calculate delay with exponential backoff (max 10 seconds)
        const delay = Math.min(baseDelay * Math.pow(1.5, attempts), 10000);

        // Wait before next attempt
        await new Promise((resolve) => setTimeout(resolve, delay));

        attempts++;
      } catch (error) {
        console.error(`Polling attempt ${attempts + 1} failed:`, error);
        attempts++;

        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, baseDelay));
      }
    }

    // Max attempts reached
    onUpdate("timeout");
    return null;
  },

  async getSubmissionChat(
    submissionId: string,
  ): Promise<SubmissionChatResponse> {
    const data = await api.get<SubmissionChatResponse>(
      `/projects/submissions/${submissionId}/chat`
    );
    return data;
  },

  async sendSubmissionChatMessage(
    submissionId: string,
    message: string,
    clientId?: string,
  ): Promise<SendSubmissionChatMessageResponse> {
    const data = await api.post<SendSubmissionChatMessageResponse>(
      `/projects/submissions/${submissionId}/chat/messages`,
      { message, client_id: clientId }
    );
    return data;
  },

  async streamSubmissionChatMessage(
    submissionId: string,
    message: string,
    callbacks: SubmissionChatStreamCallbacks,
    clientId?: string,
  ): Promise<void> {
    const response = await api.stream(
      `/projects/submissions/${submissionId}/chat/messages/stream`,
      {
        method: "POST",
        body: JSON.stringify({ message, client_id: clientId }),
      },
    );
    await this.readSubmissionChatEventStream(response, callbacks);
  },

  async subscribeSubmissionChat(
    submissionId: string,
    clientId: string,
    callbacks: SubmissionChatStreamCallbacks,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await api.stream(
      `/projects/submissions/${submissionId}/chat/events?client_id=${encodeURIComponent(
        clientId,
      )}`,
      {
        method: "GET",
        signal,
      },
    );
    await this.readSubmissionChatEventStream(response, callbacks);
  },

  async readSubmissionChatEventStream(
    response: Response,
    callbacks: SubmissionChatStreamCallbacks,
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Chat stream response was empty");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    const processLine = (line: string): void => {
      const event = parseSubmissionChatStreamEvent(line);
      if (event) {
        dispatchSubmissionChatStreamEvent(event, callbacks);
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      lines.forEach(processLine);
    }

    buffer += decoder.decode();
    if (buffer.trim()) {
      processLine(buffer);
    }
  },

  async clearSubmissionChat(
    submissionId: string,
    clientId?: string,
  ): Promise<SubmissionChatResponse> {
    const data = await api.post<SubmissionChatResponse>(
      `/projects/submissions/${submissionId}/chat/clear`,
      { client_id: clientId }
    );
    return data;
  },
};
